import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, ShieldQuestion } from 'lucide-react';
import { reconcilePosition } from '../services/api';

/**
 * DriftBanner — surfaces the result of position-ledger drift detection on
 * any bot dashboard. Three visual states:
 *
 *   1. Drift detected (short) → red banner + "Reconcile at current price" button
 *      Fires when the exchange wallet holds LESS than the platform tracks
 *      (user sold/transferred externally).
 *
 *   2. Verified (in-sync OR balance check failed gracefully) → small badge,
 *      no scary UI.
 *
 *   3. Unverifiable → quiet info note ("balance check unavailable").
 *
 * Props:
 *   drift   — the `drift` object returned by getBotStats / getTradeStats
 *   asset   — base asset symbol (e.g. "ETH"), used for display + reconcile call
 *   onReconciled — callback fired after a successful reconcile so the parent
 *                  can reload its stats
 */
export default function DriftBanner({ drift, asset, onReconciled }) {
  const [reconciling, setReconciling] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!drift) return null;

  async function handleReconcile() {
    if (reconciling) return;
    if (!window.confirm(
      `Reconcile ${asset} on ${drift.exchangeId}?\n\n` +
      `This will synthesize close events at the current market price for ${Math.abs(drift.driftAbsolute || 0).toFixed(8)} ${asset} ` +
      `to bring the bot ledger in line with your actual exchange balance. ` +
      `It cannot be undone. Continue?`
    )) return;
    setReconciling(true);
    setError(null);
    try {
      const res = await reconcilePosition({ exchangeId: drift.exchangeId, asset });
      setResult(res?.data || res);
      onReconciled?.();
    } catch (e) {
      setError(e?.message || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  }

  // Verified, in-sync — small green badge
  if (drift.verified && drift.driftDirection === 'in-sync') {
    return (
      <div className="flex items-center gap-2 text-[11px] text-emerald-300/80 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
        <span>
          Balance verified · Exchange {asset}: <span className="font-mono">{drift.actualBalance?.toFixed(8)}</span> matches tracked ledger ({drift.balanceSource})
        </span>
      </div>
    );
  }

  // Wallet has MORE than expected — user deposited extra; informational
  if (drift.verified && drift.driftDirection === 'long') {
    return (
      <div className="flex items-start gap-2 text-[11px] text-blue-300/80 bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2">
        <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
        <span>
          Your {drift.exchangeId} {asset} balance ({drift.actualBalance?.toFixed(8)}) is <strong>greater</strong> than what your bots track ({drift.totalTrackedQty?.toFixed(8)}). This is fine — likely you deposited additional {asset} that no bot has claimed. The extra is yours to use.
        </span>
      </div>
    );
  }

  // Balance unverifiable — couldn't reach exchange or no API keys
  if (!drift.verified) {
    return (
      <div className="flex items-start gap-2 text-[11px] text-gray-400 bg-dark-700/30 border border-dark-700/40 rounded-lg px-3 py-2">
        <ShieldQuestion className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
        <span>
          Balance check unavailable for {drift.exchangeId} {asset} ({drift.balanceReason || drift.balanceSource}). Tracked qty: {drift.totalTrackedQty?.toFixed(8)}. The "Unrealized exposure" below assumes the {asset} is still in your {drift.exchangeId} wallet — if you sold or transferred it externally, the numbers are stale.
        </span>
      </div>
    );
  }

  // Real drift detected (wallet short) — the alarm bell
  const shortAmount = Math.abs(drift.driftAbsolute);
  const shortPct = Math.abs(drift.driftPct);
  return (
    <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-4">
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-bold text-red-300">External wallet activity detected</p>
          <p className="text-xs text-red-200/90 mt-1 leading-relaxed">
            Your {drift.exchangeId} wallet holds <strong>{drift.actualBalance?.toFixed(8)} {asset}</strong>, but Haythix bots expect <strong>{drift.totalTrackedQty?.toFixed(8)} {asset}</strong> based on their internal ledgers. That's <strong>{shortAmount.toFixed(8)} {asset} ({shortPct.toFixed(2)}%)</strong> missing from where the platform thought it was.
          </p>
          <p className="text-xs text-red-200/70 mt-2 leading-relaxed">
            This usually means you sold, transferred, or were liquidated outside of Haythix (Coinbase mobile app, manual transfer, exchange-side stop, another bot, etc.). Until reconciled, the "Unrealized exposure" cards below are <strong>stale</strong> — they assume the {asset} is still in your wallet.
          </p>
        </div>
      </div>

      {!result && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <button
            onClick={handleReconcile}
            disabled={reconciling}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-xs font-bold disabled:opacity-40"
          >
            {reconciling
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reconciling…</>
              : <>Reconcile at current price</>}
          </button>
          <p className="text-[10px] text-red-200/60">
            Closes {shortAmount.toFixed(8)} {asset} across affected bots at the current market price. Synthetic fills tagged "external" — appear in tax report.
          </p>
        </div>
      )}

      {error && (
        <p className="mt-2 text-[11px] text-red-300">Error: {error}</p>
      )}

      {result?.reconciled && (
        <div className="mt-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-emerald-200">
              <p className="font-semibold">Reconciled.</p>
              <p className="mt-1 leading-relaxed">
                Closed <strong>{result.totalQtyClosed?.toFixed(8)} {asset}</strong> at <strong>${result.closePriceUsd?.toFixed(2)}</strong> across {result.closes?.length || 0} bot{(result.closes?.length || 0) === 1 ? '' : 's'}. The bot dashboards will refresh shortly with the corrected open exposure. The synthetic close fills are tagged <span className="font-mono">external</span> and will appear in your Tax Report as taxable events.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
